'use client';

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { Loader2 } from 'lucide-react';
import { usePathname, useSearchParams } from 'next/navigation';
import { OPEN_SIGN_IN_EVENT, type OpenSignInDetail } from '@/lib/sign-in';
import { safeInternalPath } from '@/lib/safe-url';

// The form is a chunk of its own: this dialog is on every page, and the form
// (with its email-code step) was 14 KB of JavaScript every page parsed at
// load for the few visitors who sign in. It is fetched once the page has
// finished loading and the browser is idle, so it is there by the time
// anyone presses "Sign in" - or at that moment, if they are quicker.
const loadSignInForm = () => import('./SignInForm');
const SignInForm = dynamic(loadSignInForm, {
  ssr: false,
  loading: () => (
    <div role="status" className="py-16 flex justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-gold" />
    </div>
  ),
});

// The one sign-in form of the public site, in a native <dialog> over whatever
// page the visitor is on: no page of its own and no footer under it. Opened by
// openSignIn() or by ?signin=1 in the address (where /login and the redirects
// of pages that need an account send people).
export default function SignInDialog({ lang }: { lang: string }) {
  const titleId = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  // A fresh form each time the dialog opens, and none before the first open.
  const [openCount, setOpenCount] = useState(0);
  const next = useRef<string | null>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const show = useCallback((target?: string | null) => {
    next.current = target || null;
    setOpenCount((n) => n + 1);
    if (!dialog.current?.open) dialog.current?.showModal();
  }, []);

  const close = () => dialog.current?.close();

  // Following a link out of it ("Forgot password?") leaves it behind: the
  // layout, and this dialog with it, stays mounted across pages.
  useEffect(() => {
    dialog.current?.close();
  }, [pathname]);

  useEffect(() => {
    let idle: number | undefined;
    const warm = () => {
      const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
      if (w.requestIdleCallback) idle = w.requestIdleCallback(() => void loadSignInForm(), { timeout: 10_000 });
      else void loadSignInForm();
    };
    // After the load event and a moment more: nothing the page needs to
    // show itself waits behind it.
    const start = () => window.setTimeout(warm, 3000);
    let timer: number | undefined;
    if (document.readyState === 'complete') timer = start();
    else window.addEventListener('load', () => (timer = start()), { once: true });
    return () => {
      window.clearTimeout(timer);
      const w = window as Window & { cancelIdleCallback?: (id: number) => void };
      if (idle !== undefined) w.cancelIdleCallback?.(idle);
    };
  }, []);

  useEffect(() => {
    const onOpen = (e: Event) => show((e as CustomEvent<OpenSignInDetail>).detail?.next);
    window.addEventListener(OPEN_SIGN_IN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SIGN_IN_EVENT, onOpen);
  }, [show]);

  useEffect(() => {
    if (searchParams.get('signin') !== '1') return;
    const target = searchParams.get('next');
    // Out of the address once read, so that a reload or a copied link does
    // not bring the dialog back.
    const url = new URL(window.location.href);
    url.searchParams.delete('signin');
    url.searchParams.delete('next');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    show(target);
  }, [searchParams, show]);

  // A full load rather than a client refresh: the header menu, favourites and
  // every other part of the page that asked "who is this?" ask again.
  const onSignedIn = () => {
    const target = safeInternalPath(next.current, '');
    if (target) window.location.assign(target);
    else window.location.reload();
  };

  return (
    <dialog
      ref={dialog}
      aria-labelledby={titleId}
      onClick={(e) => {
        // A click on the backdrop (the dialog element itself) closes it.
        if (e.target === dialog.current) close();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
        }
      }}
      className="m-auto w-[min(26rem,calc(100vw-1.25rem))] max-h-[calc(100dvh-1.25rem)] overflow-y-auto overflow-x-hidden overscroll-contain rounded-2xl p-0 bg-neutral-900 text-white border border-neutral-800 shadow-2xl backdrop:bg-black/60 backdrop:backdrop-blur-sm"
    >
      <div className="p-4 sm:p-6">
        {openCount > 0 && (
          <SignInForm key={openCount} lang={lang} titleId={titleId} onClose={close} onSignedIn={onSignedIn} />
        )}
      </div>
    </dialog>
  );
}
